<template>
  <v-container v-scroll="onScroll" class="px-3 px-sm-6 mt-2" fluid>
    <v-row class="mb-12">
      <template v-if="me && $asa.hasAnActiveSubscription()">
        <v-col cols="12">
          <v-row class="align-end" no-gutters>
            <v-col cols="12" sm="8" lg="10">
              <h1 class="text-h4 font-weight-bold">Public Critiques</h1>
              <h2 class="text-subtitle-1 mb-0">
                Browse critiques reviewed by our team of critics
                <v-dialog v-model="critiquesDialog" width="400">
                  <template #activator="{ on }">
                    <v-btn icon small class="ml-2" v-on="on">
                      <v-icon small>fal fa-info-circle</v-icon>
                    </v-btn>
                  </template>
                  <v-card outlined>
                    <v-card-title class="text-h5" primary-title>
                      What are Critiques?
                    </v-card-title>
                    <v-card-text>
                      <p>
                        In photography, we have used the term “photo critique” to
                        judge an image and provide feedback to the photographer
                        about that image.
                      </p>
                      <p>
                        Photo critiques offer a deconstruction of your photo
                        looking at lighting, colours, composition, sharpness
                        (where relevant) and storytelling. They are great ways to
                        get a new perspective on your photography.
                      </p>
                      <p>
                        We give constructive feedback to improve your photography
                        regardless of skill level. It doesn't matter if you are a
                        beginner,a picture-taker or a professional photographer.
                      </p>
                      <p>
                        <nuxt-link to="/learning/professional-critiques"
                          >Learn more</nuxt-link
                        >
                      </p>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn text small @click="critiquesDialog = false">
                        Close
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </h2>
            </v-col>
            <v-col class="text-right" cols="12" sm="4" lg="2">
              <Filters
                :categories="categories"
                :on-apply="onApply"
              />
            </v-col>
          </v-row>
        </v-col>
      </template>
      <template v-else>
        <v-col cols="12">
          <div class="text-center mt-12">
            <h2 class="text-h4 font-weight-bold mb-4">
              The Perfect Way To Learn Photography
            </h2>
            <p class="text-body-1 mb-12">
              As personal photo coaches, we give constructive feedback to improve your photography regardless of skill level
            </p>
          </div>
        </v-col>
      </template>
      <v-col cols="12" class="px-0">
        <div class="mb-6">
          <v-row
            :class="{
              'justify-center': !me || (me && !$asa.hasAnActiveSubscription())
            }"
          >
            <v-col
              v-for="(critique, index) in critiques"
              :key="index"
              cols="12"
              md="6"
              lg="4"
              xl="3"
            >
              <v-card
                :to="`/public-critiques/${critique.id}/${critique.slug}?st=${offsetTop}`"
                outlined
              >
                <v-img
                  :src="$asa.getImageUrl(critique.thumbnail, 'thumb')"
                  :alt="`${critique.title}.`"
                  :class="$vuetify.theme.dark ? 'dark' : ''"
                  class="thumb-preview"
                  width="100%"
                  contain
                  min-height="300px"
                  max-height="300px"
                >
                  <template #placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular
                        indeterminate
                        color="tertiary"
                      ></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
                <v-card-text class="pt-4">
                  <div>
                    <h2
                      class="
                        text-h6
                        mb-1
                        minor-line-height
                        text-truncate
                        font-weight-bold
                      "
                    >
                      {{ critique.title }}
                    </h2>
                    <h3 class="text-body-2 mb-2">
                      Category:
                      <span class="font-weight-bold">{{
                        types.categories[critique.photoCategoryId]
                      }}</span>
                    </h3>
                    <h3 class="text-body-1">
                      Photographer level:
                      <span class="font-weight-bold">{{
                        critique.photographerLevel
                      }}</span>
                    </h3>
                    <h3 class="text-caption">
                      <span
                        v-if="
                          critique.status === types.critique.status.reviewed
                        "
                      >
                        Reviewed {{ critique.completedAt | timeago }}
                      </span>
                      <span v-else>
                        Submitted {{ critique.createdAt | timeago }}
                      </span>
                    </h3>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>
        <div v-intersect="onIntersect" />
        <div v-if="loading" class="text-center">
          <v-icon class="fa-spin my-3"> fal fa-spinner </v-icon>
        </div>
        <div
          v-if="!me || (me && !$asa.hasAnActiveSubscription())"
          class="text-center mt-12"
        >
          <p class="text-h6">
            These are just some critiques example. Get a
            <span class="font-weight-bold">Pro membership</span> to view all of
            them
          </p>
          <v-btn to="/membership" depressed x-large dark class="h-60 ma-0 mt-6">
            Get Pro
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment'
import goTo from 'vuetify/lib/services/goto'
import { mapState } from 'vuex'
import Filters from '@/components/critiques/Filters.vue'
import types from '@/utils/types'

export default {
  filters: {
    timeago: date => {
      return moment.utc(date).local().fromNow()
    }
  },
  components: {
    Filters
  },
  middleware: 'header',
  data() {
    return {
      types,
      loading: false,
      offsetTop: 0,
      critiquesDialog: false,
      goToTimeoutId: undefined
    }
  },
  async fetch({ store, $asa }) {
    try {
      await store.dispatch('account/getCategories')
    } catch (error) {
      $asa.appInsights('Pages: public-critiques/index.vue - method: fetch', error)
    }
  },
  head({ $seo }) {
    return $seo({
      title: 'Public Critiques',
      description: 'Browse high-quality photo critiquesmade by professional photographers.'
    })
  },
  computed: {
    ...mapState({
      me: state => state.auth.user,
      critiques: state => state.critiques.critiques,
      isLastPage: state => state.critiques.critiquesIsLastPage,
      page: state => state.critiques.critiquesPage,
      critiquesLoadDate: state => state.critiques.critiquesLoadDate,
      categories: state => state.account.categories
    })
  },
  destroyed() {
    if (this.goToTimeoutId) {
      clearTimeout(this.goToTimeoutId)
    }
  },
  async mounted() {
    // Reload the gallery after 30 minutes in cache
    if (
      !this.loadDate ||
      moment().isAfter(moment(this.loadDate).add(30, 'minutes'))
    ) {
      this.$store.commit('critiques/RESET_PUBLIC_CRITIQUES')
    }

    if (this.critiques.length === 0 || this.$route.query.back !== 'yes') {
      try {
        this.loading = true
        this.$store.commit('critiques/RESET_PUBLIC_CRITIQUES')
        const data = {
          page: this.page
        }
        await this.$store.dispatch('critiques/getPublicCritiques', data)
        const date = new Date()
        this.$store.commit(
          'critiques/SET_PUBLIC_CRITIQUES_LOAD_DATE',
          moment(date)
        )
      } catch (error) {
        this.$asa.appInsights('Pages: public-critiques/index.vue - method: mounted', error)
      } finally {
        this.loading = false
      }
    }
    this.goToTimeoutId = setTimeout(() => {
      if (this.$route.query.st && this.critiques.length > 0) {
        goTo(Number(this.$route.query.st))
      }
    })
  },
  methods: {
    async loadMore() {
      this.loading = true
      try {
        await this.$store.dispatch('critiques/getPublicCritiques', {
          page: this.page + 1
        })
      } catch (error) {
        this.$asa.appInsights('Pages: public-critiques/index.vue - method: loadMore', error)
      } finally {
        this.loading = false
      }
    },
    onScroll() {
      setTimeout(() => {
        const top = document.getElementById('app').getBoundingClientRect().top * -1
        this.offsetTop = top
      })
    },
    async onApply(filters) {
      this.$store.commit('critiques/RESET_PUBLIC_CRITIQUES')
      this.$store.commit('critiques/SET_FILTERS', filters)
      const data = {
        page: this.page
      }
      try {
        this.loading = true
        await this.$store.dispatch('critiques/getPublicCritiques', data)
        const date = new Date()
        this.$store.commit(
          'critiques/SET_PUBLIC_CRITIQUES_LOAD_DATE',
          moment(date)
        )
      } catch (error) {
        this.$asa.appInsights('Pages: public-critiques/index.vue - method: onApply', error)
      } finally {
        this.loading = false
      }
    },
    onIntersect(entries) {
      if (
        entries[0].intersectionRatio > 0 &&
        !this.isLastPage &&
        this.critiques.length !== 0 &&
        !this.loading
      ) {
        this.loadMore()
      }
    }
  }
}
</script>

<style scoped>
.thumb-preview {
  background-color: #212121;
  border: 2px solid rgba(0, 0, 0, 0.54);
}
.thumb-preview.dark {
  background-color: #000;
}
</style>
